import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LogIn, UserPlus } from "lucide-react";
import { toast } from "sonner";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export const LinkAmazonLogin = () => {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "", name: "", phone: "" });

  // Forgot password state
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        const response = await axios.post(`${API}/linkamazon/auth/login`, {
          email: formData.email,
          password: formData.password
        });
        if (response.data.token) {
          localStorage.setItem("buyer_token", response.data.token);
          localStorage.setItem("buyer_user", JSON.stringify(response.data.user));
          if (response.data.user.phone) {
            localStorage.setItem("buyer_phone", response.data.user.phone);
          }
          toast.success("Koneksyon reyisi!");
          navigate("/linkamazon/dashboard");
        }
      } else {
        const response = await axios.post(`${API}/linkamazon/auth/register`, {
          email: formData.email,
          password: formData.password,
          name: formData.name,
          phone: formData.phone
        });
        if (response.data.success) {
          toast.success("Kont kreye! Ou ka konekte kounye a.");
          setIsLogin(true);
          setFormData({ ...formData, name: "", phone: "" });
        }
      }
    } catch (error) {
      if (error.response) {
        // Sèvè a reponn ak yon erè (400, 401, 500...)
        const detail = error.response.data?.detail;
        const errorMsg = typeof detail === "string"
          ? detail
          : Array.isArray(detail)
            ? detail.map(d => d.msg || d.message || JSON.stringify(d)).join(", ")
            : `Erè ${error.response.status}`;
        toast.error(errorMsg);
      } else if (error.request) {
        // Demann voye men pa gen repons - pwoblèm rezò
        toast.error("Pa kapab jwenn sèvè a. Tcheke koneksyon entènèt ou a epi eseye ankò.");
      } else {
        // Lòt erè
        toast.error("Erè ennatandi. Tanpri eseye ankò.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Send reset link
  const handleForgotSend = async (e) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setForgotLoading(true);
    try {
      await axios.post(`${API}/linkamazon/auth/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erè nan voye imèl");
    } finally {
      setForgotLoading(false);
    }
  };

  const closeForgot = () => {
    setShowForgot(false);
    setForgotSent(false);
    setForgotEmail("");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FF9900] to-[#FFB84D] flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-2xl border-0">
        {/* Logo / Heading */}
        <div className="text-center pt-8 pb-2 px-6">
          <div className="mx-auto mb-4">
            <img src="/logo.png" alt="BVGLINK Logo" className="max-h-32 mx-auto object-contain" />
          </div>
          <h1 className="text-2xl font-black text-[#232F3E] mb-1">BVGLINK Hub</h1>
          <p className="text-gray-600">{isLogin ? "Konekte pou achte" : "Kreye yon kont"}</p>
        </div>

        <CardContent className="px-6 pb-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <>
                <div>
                  <Label>Non Konplè</Label>
                  <Input
                    type="text"
                    placeholder="Jean Baptiste"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required={!isLogin}
                    data-testid="buyer-name"
                  />
                </div>
                <div>
                  <Label>Telefòn</Label>
                  <Input
                    type="tel"
                    placeholder="+509 1234 5678"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    data-testid="buyer-phone"
                  />
                </div>
              </>
            )}

            <div>
              <Label>Imèl</Label>
              <Input
                type="email"
                placeholder="ou@egzanp.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="buyer-email"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>Modpas</Label>
                {isLogin && (
                  <button
                    type="button"
                    onClick={() => setShowForgot(true)}
                    className="text-sm text-[#FF9900] hover:underline font-medium"
                    data-testid="forgot-password-link"
                  >
                    Modpas bliye?
                  </button>
                )}
              </div>
              <Input
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                minLength={6}
                data-testid="buyer-password"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-[#FF9900] hover:bg-[#E88B00] text-white py-6 text-lg font-bold"
              disabled={loading}
              data-testid="buyer-submit"
            >
              {loading ? (
                "Ap trete..."
              ) : isLogin ? (
                <><LogIn className="w-5 h-5 mr-2" />Konekte</>
              ) : (
                <><UserPlus className="w-5 h-5 mr-2" />Kreye Kont</>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-[#FF9900] hover:underline font-medium"
            >
              {isLogin ? "Pa gen kont? Kreye youn" : "Deja gen kont? Konekte"}
            </button>
          </div>
        </CardContent>
      </Card>

      {/* ====== FORGOT PASSWORD MODAL ====== */}
      {showForgot && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(6px)" }}
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-[#FF9900] to-[#FFB84D] p-6 text-center relative">
              <div className="text-3xl mb-1">{forgotSent ? "📬" : "🔑"}</div>
              <h2 className="text-white font-bold text-xl">
                {forgotSent ? "Imèl Voye!" : "Modpas Bliye?"}
              </h2>
              <button
                onClick={closeForgot}
                className="absolute top-3 right-4 text-white/70 hover:text-white text-2xl leading-none"
                data-testid="close-forgot-modal"
              >
                ×
              </button>
            </div>

            <div className="p-6">
              {!forgotSent ? (
                /* Enter email form */
                <form onSubmit={handleForgotSend} className="space-y-4">
                  <p className="text-gray-500 text-sm text-center">
                    Antre imèl kont ou a. N ap voye yon{" "}
                    <strong className="text-gray-700">lyen</strong> pou ou chanje modpas ou.
                  </p>

                  <div>
                    <Label className="text-gray-700">Imèl Kont Ou</Label>
                    <Input
                      type="email"
                      placeholder="ou@egzanp.com"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      required
                      autoFocus
                      data-testid="forgot-email-input"
                      className="mt-1"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={forgotLoading || !forgotEmail}
                    className="w-full bg-[#FF9900] hover:bg-[#E88B00] text-white font-bold py-3"
                    data-testid="forgot-send-btn"
                  >
                    {forgotLoading ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Ap voye...
                      </span>
                    ) : (
                      "📧 Voye Lyen la"
                    )}
                  </Button>

                  <button
                    type="button"
                    onClick={closeForgot}
                    className="w-full text-gray-400 hover:text-gray-600 text-sm py-1"
                  >
                    Anile
                  </button>
                </form>
              ) : (
                /* Success: email sent */
                <div className="text-center space-y-4 py-2">
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                    <p className="text-green-700 font-semibold text-sm">✅ Nou voye yon lyen bay:</p>
                    <p className="text-green-800 font-bold mt-1 break-all">{forgotEmail}</p>
                  </div>

                  <div className="text-left bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-1">
                    <p className="text-amber-800 text-sm font-semibold">📋 Etap pou suiv:</p>
                    <p className="text-amber-700 text-sm">1. Ouvri imèl ou a</p>
                    <p className="text-amber-700 text-sm">
                      2. Klike sou <strong>"Reyinisyalize Modpas Mwen"</strong>
                    </p>
                    <p className="text-amber-700 text-sm">3. Mete yon nouvo modpas</p>
                    <p className="text-amber-600 text-xs mt-2">
                      ⏰ Lyen an valid pou <strong>1 è</strong> sèlman.
                      <br />
                      Tcheke dosye <strong>Spam</strong> si ou pa wè l.
                    </p>
                  </div>

                  <Button
                    onClick={closeForgot}
                    className="w-full bg-[#FF9900] hover:bg-[#E88B00] text-white font-bold py-3"
                  >
                    Fèmen
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

